import { useState } from 'react';
import * as S from '../../domain/sceneOps';
import { SCENE_MAX_SIZE, SCENE_MIN_SIZE, type Scene } from '../../domain/schemas';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { SCENE_PRESETS, SCENE_PRESET_IDS, buildPresetScene } from '../../scene/presets';
import { Button, ConfirmDialog, Panel } from '../ui';
import type { EditFn } from './useSceneEdit';

const sel = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const SIZES: [string, number, number][] = [
  ['HD 1280×720', 1280, 720],
  ['Full HD 1920×1080', 1920, 1080],
  ['Square 1080×1080', 1080, 1080],
  ['Portrait 1080×1920', 1080, 1920],
];

/** Scene list, presets, canvas size and background (colour, gradient, image). */
export function ScenePanel({ scene, edit }: { scene: Scene; edit: EditFn }) {
  const { project } = useProject();
  const { selection } = useServices();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const sid = scene.id;
  const images = project.assets.filter((a) => a.kind === 'image');
  const switchTo = (id: string) => {
    if (edit('Switch scene', (p) => S.setActiveScene(p, id))) selection.selectObjects([]);
  };

  return (
    <Panel title="Scene" headingLevel={2}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-0.5">
            <label htmlFor="scene-select" className="text-[11px] text-zinc-400">
              Scene
            </label>
            <select id="scene-select" className={sel} value={sid} onChange={(e) => switchTo(e.target.value)} data-testid="scene-select">
              {project.scenes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="scene-name" className="text-[11px] text-zinc-400">
              Name
            </label>
            <input
              id="scene-name"
              className={sel}
              value={name ?? scene.name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                if (name !== null && name.trim() && name !== scene.name) edit('Rename scene', (p) => S.renameScene(p, sid, name));
                setName(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
          </div>
          <Button size="sm" onClick={() => edit('New scene', (p) => S.addScene(p)) && selection.selectObjects([])}>
            New blank
          </Button>
          <Button size="sm" onClick={() => edit('Duplicate scene', (p) => S.duplicateScene(p, sid)) && selection.selectObjects([])}>
            Duplicate
          </Button>
          <Button size="sm" variant="ghost" className="text-red-300" disabled={project.scenes.length <= 1} onClick={() => setConfirmDelete(true)}>
            Delete scene
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Scene presets">
          <span className="text-xs text-zinc-400">New from preset:</span>
          {SCENE_PRESET_IDS.map((id) => (
            <Button key={id} size="sm" title={SCENE_PRESETS[id].description} onClick={() => edit(`New ${SCENE_PRESETS[id].label}`, (p) => S.addScene(p, buildPresetScene(id))) && selection.selectObjects([])} data-testid={`preset-${id}`}>
              {SCENE_PRESETS[id].label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-0.5">
            <label htmlFor="scene-w" className="text-[11px] text-zinc-400">
              Width
            </label>
            <input id="scene-w" type="number" min={SCENE_MIN_SIZE} max={SCENE_MAX_SIZE} className={`${sel} w-24`} value={scene.width} onChange={(e) => Number(e.target.value) >= SCENE_MIN_SIZE && edit('Scene size', (p) => S.setSceneSize(p, sid, Number(e.target.value), scene.height), 'scene-size')} />
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="scene-h" className="text-[11px] text-zinc-400">
              Height
            </label>
            <input id="scene-h" type="number" min={SCENE_MIN_SIZE} max={SCENE_MAX_SIZE} className={`${sel} w-24`} value={scene.height} onChange={(e) => Number(e.target.value) >= SCENE_MIN_SIZE && edit('Scene size', (p) => S.setSceneSize(p, sid, scene.width, Number(e.target.value)), 'scene-size')} />
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor="scene-size-preset" className="text-[11px] text-zinc-400">
              Size preset
            </label>
            <select id="scene-size-preset" className={sel} value="" onChange={(e) => {
              const s = SIZES.find(([l]) => l === e.target.value);
              if (s) edit('Scene size', (p) => S.setSceneSize(p, sid, s[1], s[2]));
            }}>
              <option value="">Choose…</option>
              {SIZES.map(([l]) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input id="scene-bg" type="color" value={scene.background} onChange={(e) => edit('Background colour', (p) => S.setSceneBackground(p, sid, { background: e.target.value }), 'bg')} className="h-8 w-10 cursor-pointer rounded border border-zinc-700 bg-transparent" />
            <label htmlFor="scene-bg" className="text-xs text-zinc-300">
              Background <span className="font-mono text-zinc-400">{scene.background}</span>
            </label>
          </div>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={!!scene.gradient} onChange={(e) => edit('Gradient', (p) => S.setSceneBackground(p, sid, { gradient: e.target.checked ? { to: '#000000', angle: 90 } : null }))} />
            Gradient
          </label>
          {scene.gradient && (
            <>
              <div className="flex items-center gap-2">
                <input id="scene-grad" type="color" value={scene.gradient.to} onChange={(e) => edit('Gradient', (p) => S.setSceneBackground(p, sid, { gradient: { ...scene.gradient!, to: e.target.value } }), 'grad')} className="h-8 w-10 cursor-pointer rounded border border-zinc-700 bg-transparent" />
                <label htmlFor="scene-grad" className="text-xs text-zinc-300">
                  to
                </label>
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="scene-angle" className="text-[11px] text-zinc-400">
                  Direction {scene.gradient.angle}°
                </label>
                <input id="scene-angle" type="range" min={0} max={360} step={15} value={scene.gradient.angle} onChange={(e) => edit('Gradient', (p) => S.setSceneBackground(p, sid, { gradient: { ...scene.gradient!, angle: Number(e.target.value) } }), 'grad-angle')} />
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-0.5">
            <label htmlFor="scene-bg-image" className="text-[11px] text-zinc-400">
              Background image
            </label>
            <select
              id="scene-bg-image"
              className={sel}
              value={scene.backgroundImage?.assetId ?? ''}
              onChange={(e) => edit('Background image', (p) => S.setBackgroundImage(p, sid, e.target.value ? { assetId: e.target.value, fit: scene.backgroundImage?.fit ?? 'cover', scale: 1, offsetX: 0, offsetY: 0, opacity: 1 } : null))}
            >
              <option value="">None</option>
              {images.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          {scene.backgroundImage && (
            <>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="scene-bg-fit" className="text-[11px] text-zinc-400">
                  Fit
                </label>
                <select id="scene-bg-fit" className={sel} value={scene.backgroundImage.fit} onChange={(e) => edit('Background image', (p) => S.setBackgroundImage(p, sid, { ...scene.backgroundImage!, fit: e.target.value as 'cover' }))}>
                  <option value="cover">Cover</option>
                  <option value="contain">Contain</option>
                  <option value="stretch">Stretch</option>
                  <option value="center">Original size</option>
                </select>
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="scene-bg-scale" className="text-[11px] text-zinc-400">
                  Zoom {scene.backgroundImage.scale.toFixed(2)}×
                </label>
                <input id="scene-bg-scale" type="range" min={0.1} max={4} step={0.05} value={scene.backgroundImage.scale} onChange={(e) => edit('Background image', (p) => S.setBackgroundImage(p, sid, { ...scene.backgroundImage!, scale: Number(e.target.value) }), 'bg-scale')} />
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="scene-bg-x" className="text-[11px] text-zinc-400">
                  Offset X
                </label>
                <input id="scene-bg-x" type="range" min={-scene.width} max={scene.width} value={scene.backgroundImage.offsetX} onChange={(e) => edit('Background image', (p) => S.setBackgroundImage(p, sid, { ...scene.backgroundImage!, offsetX: Number(e.target.value) }), 'bg-x')} />
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="scene-bg-y" className="text-[11px] text-zinc-400">
                  Offset Y
                </label>
                <input id="scene-bg-y" type="range" min={-scene.height} max={scene.height} value={scene.backgroundImage.offsetY} onChange={(e) => edit('Background image', (p) => S.setBackgroundImage(p, sid, { ...scene.backgroundImage!, offsetY: Number(e.target.value) }), 'bg-y')} />
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor="scene-bg-op" className="text-[11px] text-zinc-400">
                  Opacity {Math.round(scene.backgroundImage.opacity * 100)}%
                </label>
                <input id="scene-bg-op" type="range" min={0} max={1} step={0.05} value={scene.backgroundImage.opacity} onChange={(e) => edit('Background image', (p) => S.setBackgroundImage(p, sid, { ...scene.backgroundImage!, opacity: Number(e.target.value) }), 'bg-op')} />
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmDelete}
        danger
        title={`Delete scene “${scene.name}”?`}
        body="Its objects are removed. Characters and images stay in the project. You can undo this."
        confirmLabel="Delete scene"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          if (edit('Delete scene', (p) => S.deleteScene(p, sid))) selection.selectObjects([]);
        }}
      />
    </Panel>
  );
}
